import React, { Component } from "react";
import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import { EjariProperty, EjariPropertyType, Lease, } from "@eres/ejari-api/Entity";
import { IStore } from "@my-dld/reducers/store";
import { connect, ConnectedProps } from "react-redux";
import { TabuPropertyType } from "@my-dld/utils/PropertyUtils";
import { UserDataState } from "@my-dld/reducers/UserData";
import { attachLeasToProperty, EjariPropertyFromApi } from "@eres/ejari-api/Utils";
import { LocaleValue } from "@eres/core/dist/LocaleValue";
import { Property } from "@my-dld/screens/MyDLD/Portfolio/MollakMeydaanAPI/entities";

export interface XInfo {
	count: number;
	value: number;
	percent?: number;
}

export interface TopInfo {
	all: XInfo;
	lands?: XInfo;
	units?: XInfo;
	villas?: XInfo;
	buildings?: XInfo;
}

export interface Portfolio<X> {
	all?: X[];
	lands?: X[];
	villas?: X[];
	buildings?: X[];
	units?: X[];
	info: TopInfo;
	isLoading: boolean;
	version: number;
}

export interface ProxyPortfolio<X> extends Portfolio<X> {
	id: string | number;
	name: LocaleValue;
}

export interface IPortfolioContext {
	assets: Portfolio<AssetCard>;
	proxyAssets: ProxyPortfolio<AssetCard>[];
	leases: Portfolio<EjariProperty>;
	isLoading: boolean;
	mapProperties: { properties: Property[]; isLoading: boolean };
}

const defaultContext: IPortfolioContext = {
	isLoading: false,
	assets: {
		isLoading: false,
		version: 0,
		info: {
			all: {
				value: 0,
				count: 0,
			},
		}
	},
	proxyAssets: [],
	leases: {
		version: 0,
		isLoading: false,
		info: {
			all: {
				value: 0,
				count: 0,
			},
		}
	},
	mapProperties: { properties: [], isLoading: false },
};

export const PortfolioContext = React.createContext( defaultContext );


interface Props extends IPortfolioContext {

}

class PortfolioContextWrapper extends Component<Props> {
	constructor( props: Props ) {
		super( props );
		this.state = {
			...defaultContext,
		};
	}


	render() {
		const { children, ...props } = this.props;
		return <PortfolioContext.Provider value={ props }>
			{ children }
		</PortfolioContext.Provider>;
	}
}

const mapStateToProps = ( state: IStore ): IPortfolioContext => {
	const isLoading = state.UserData?.leaseLoading || state.UserData?.propertyLoading || state.UserData?.mapPropertiesLoading || false;
	return {
		assets: populateAssets( state.UserData ),
		leases: populateLeases( state.UserData ),
		proxyAssets: populateProxyAssets( state.UserData ),
		isLoading: isLoading,
		mapProperties: populateMapProperties( state.UserData ),
	};
};

const connector = connect( mapStateToProps );

export default connector( PortfolioContextWrapper );
export type ConnectedPortfolioProps = ConnectedProps<typeof connector>
export { connector as portfolioConnector };


function populateProxyAssets( data: UserDataState ): ProxyPortfolio<AssetCard>[] {
	const r: ProxyPortfolio<AssetCard>[] = [];
	if ( data.proxyProperties && data.proxyProperties.length > 0 ) {
		data.proxyProperties.forEach( pp => {
			const p: ProxyPortfolio<AssetCard> = {
				id: pp.id,
				name: pp.name,
				isLoading: data.propertyLoading === true,
				version: data.assetVersion,
				info: {
					all: {
						value: 0,
						count: 0,
					},
				}
			};
			if ( pp.properties && pp.properties.length > 0 ) {
				const lands = pp.properties.filter( x => x.propertyTypeId === TabuPropertyType.LAND );
				const villas = pp.properties.filter( x => x.propertyTypeId === TabuPropertyType.VILLA || x.propertyTypeId === TabuPropertyType.BUILDING );
				const units = pp.properties.filter( x => x.propertyTypeId === TabuPropertyType.UNIT );
				rx( p, "all", pp.properties, AssetValue );
				rx( p, "units", units, AssetValue );
				rx( p, "villas", villas, AssetValue );
				rx( p, "lands", lands, AssetValue );
			} else {
				p.all = [];
				p.buildings = [];
				p.lands = [];
				p.units = [];
				p.villas = [];
				p.buildings = [];
			}
			r.push( p );
		} );
	}
	return r;
}

function populateAssets( data: UserDataState ): Portfolio<AssetCard> {
	const result: Portfolio<AssetCard> = {
		isLoading: data.propertyLoading === true,
		version: data.assetVersion,
		info: {
			all: {
				value: 0,
				count: 0,
			},
		}
	};
	const cards = data.properties;
	if ( cards && cards.length > 0 ) {
		const lands = cards.filter( x => x.propertyTypeId === TabuPropertyType.LAND );
		const villas = cards.filter( x => x.propertyTypeId === TabuPropertyType.VILLA || x.propertyTypeId === TabuPropertyType.BUILDING );
		const units = cards.filter( x => x.propertyTypeId === TabuPropertyType.UNIT );
		rx( result, "all", cards, AssetValue );
		rx( result, "units", units, AssetValue );
		rx( result, "villas", villas, AssetValue );
		rx( result, "lands", lands, AssetValue );
	} else {
		result.all = [];
		result.buildings = [];
		result.lands = [];
		result.units = [];
		result.villas = [];
		result.buildings = [];
	}
	return result;
}

const LeaseFilter = ( lease: Lease, pType: number ) => {
	if ( lease && lease.Properties && lease.Properties.length && lease.Properties.length > 0 ) {
		for ( const p of lease.Properties ) {
			if ( p.PropertyTypeId === pType ) return true;
		}
	}
	return false;
};

function populateLeases( data: UserDataState ): Portfolio<EjariProperty> {
	const result: Portfolio<EjariProperty> = {
		isLoading: data.leaseLoading == true,
		version: data.leaseVersion,
		info: {
			all: {
				value: 0,
				count: 0,
			},
		}
	};
	const cards = data.leases;
	if ( cards && cards.length > 0 ) {
		const lands = cards.filter( x => LeaseFilter( x, EjariPropertyType.LAND ) );
		const building = cards.filter( x => LeaseFilter( x, EjariPropertyType.BUILDING ) || LeaseFilter( x, EjariPropertyType.VILLA ) );
		const units = cards.filter( x => LeaseFilter( x, EjariPropertyType.UNIT ) || LeaseFilter( x, EjariPropertyType.VIRTUAL_VILLA ) );
		const a = LeaseToEjariProperties( lands );
		const b = LeaseToEjariProperties( building );
		const c = LeaseToEjariProperties( units );
		const d = [ ...a, ...b, ...c ];
		rx( result, "all", d, EValue );
		rx( result, "lands", a, EValue );
		rx( result, "buildings", b, EValue );
		rx( result, "units", c, EValue );

	} else {
		result.all = [];
		result.buildings = [];
		result.lands = [];
		result.units = [];
		result.villas = [];
		result.buildings = [];
	}
	return result;
}

function populateMapProperties( data: UserDataState ): { properties: Property[]; isLoading: boolean } {
	return {
		isLoading: data.mapPropertiesLoading == true,
		properties: data.mapProperties ?? [],
	};
}

type PTypes = "lands" | "buildings" | "villas" | "units" | "all";

function AssetValue( item: AssetCard ) {
	return item.investmentValue;
}

function EValue( item: EjariProperty ) {
	return ( item.CurrentContract && item.CurrentContract.ContractAmount ) ? Number( item.CurrentContract.ContractAmount ) : 0;
}

function rx<X>( sink: Portfolio<X>, key: PTypes, source: X[], getValue: ( x: X ) => number ) {
	if ( source && source.length > 0 ) {
		sink[ key ] = source;
		const info = sink.info;
		info[ key ] = {
			count: source.length,
			value: source.reduce( ( p, c ) => p + getValue( c ), 0 ),
		};
		if ( info.all.value > 0 )
			info[ key ]!.percent = ( info[ key ]!.value / info.all.value ) * 100.0;
	} else {
		sink[ key ] = [];
	}
}

function LeaseToEjariProperties( leases: Lease[], pType?: number ) {
	let m: any = [];
	if ( leases ) leases = JSON.parse( JSON.stringify( leases ) );
	for ( const lease of leases ) {

		if ( lease && lease.Properties && lease.Properties.length && lease.Properties.length > 0 ) {
			// let s: EjariProperty[] = [];
			// for (let prop of lease.Properties) {
			//     s.push(normalizeEjariProperty(prop, lease));
			// }
			// let pp = s.filter( (ele: any, ind: number) => ind === m.findIndex( (elem: any) => elem.EjariPropertyId != ele.EjariPropertyId));
			let p = EjariPropertyFromApi( lease.Properties[ 0 ] );
			if ( p ) {
				attachLeasToProperty( p, lease );
				m.push( p );
			}

		}
	}
	// let pp = m.filter( (ele: any, ind: number) => ind === m.findIndex( (elem: any) => elem.EjariPropertyId === ele.EjariPropertyId && elem.PropertyType === ele.PropertyType));
	return m;
}
